/**
 * A viewer's choices on this browser — which optional columns a list shows (`data-table.tsx`),
 * which sections of a record are open and show their empty fields (`record-section.tsx`) — kept
 * in local storage as one JSON object per key, the key naming the viewer.
 *
 * **A store that refuses, or holds something unreadable, leaves the defaults, and the page works
 * the same.** Local storage throws in a private window, with site data blocked, and when full; a
 * choice is a convenience, never state the page depends on, so every read and write is under
 * try/catch here, once, rather than at each caller. A write that is refused keeps the choice for
 * the page load only, in the caller's own React state.
 *
 * Extracted from the column picker, whose behaviour and key did not change, when the record
 * sections needed the same memory: two copies of the same try/catch would drift on what an
 * unreadable value means.
 */

/** The object stored under `key`, or `{}` — for no key, nothing stored, a refused read, or a value
 *  that is not a JSON object. Each member is the caller's to check. */
export function readViewerChoice(key: Nullable<string>): Record<string, unknown> {
  if (!key) return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? '{}') as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** Stores `value` under `key`; a missing key or a refused write keeps nothing, silently. */
export function writeViewerChoice(key: Nullable<string>, value: object): void {
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Kept for this page load only, by the caller.
  }
}
